import {forwardRef} from 'react';
import {FormProvider} from 'react-hook-form';
import {BrandSection, CreateBrand} from '@tinct/core-api-models';
import {Button, Card, CardContent, CardFooter} from '@tinct/web-components';
import {useCreateOrganizationBrandForm} from '../hooks/use-create-organization-brand-form';
import {createConnectedInputHelpers} from "@tinct/web-form";

/** The brand sections this form edits: its save takes these from the form, the others from the loaded version. */
const COMPLIANCE_SECTIONS: readonly BrandSection[] = ['compliance'];

type UpdateComplianceFormProps = {
  organizationId: string | null;
  onSuccess?: () => void;
  hideFooter?: boolean;
};

export const UpdateComplianceForm = forwardRef<HTMLFormElement, UpdateComplianceFormProps>(({
  hideFooter = false,
  organizationId,
  onSuccess,
}, ref) => {
  const { form, isReady, handleSubmit, isLoading } = useCreateOrganizationBrandForm({
    organizationId,
    sections: COMPLIANCE_SECTIONS,
    onSuccess,
  });

  const { ControlledTextInput, ControlledTextareaInput } = createConnectedInputHelpers<CreateBrand>();

  // The <form> (and so the ref a funnel submits through) only exists once the
  // loaded values are in it: nothing can submit the empty defaults.
  if (!isReady) {
    return <div>Loading...</div>;
  }

  return (
    <FormProvider {...form}>
      <div className="flex flex-col gap-8">
        <form ref={ref} onSubmit={handleSubmit}>
          <Card className="rounded-2xl gap-0 py-0">
            <CardContent className="px-6 pt-2">
              <div className="flex flex-col divide-y">
                <ControlledTextInput
                  className="py-6"
                  name="compliance.legalLink"
                  label="Legal Links"
                  direction="horizontal"
                  placeholder="Legal Links..."
                />
                <ControlledTextareaInput
                  className="py-6"
                  name="compliance.industryCompliance"
                  label="Industry Compliance"
                  direction="horizontal"
                  placeholder="Industry Compliance..."
                />
                <ControlledTextareaInput
                  className="py-6"
                  name="compliance.contentRestriction"
                  label="Content Restrictions"
                  direction="horizontal"
                  placeholder="Content Restrictions..."
                />
              </div>
            </CardContent>
            {!hideFooter && (
              <CardFooter className="border-t justify-end py-6">
                <Button type="submit" isLoading={isLoading}>Save</Button>
              </CardFooter>
            )}
          </Card>
        </form>
      </div>
    </FormProvider>
  );
});

UpdateComplianceForm.displayName = 'UpdateComplianceForm';
